import { useEffect, useState } from 'react';
import type { Block, BlockOf, Site } from '../model/types';
import { BLOCK_TYPES } from '../model/catalog';
import { fmtDate, fmtTime } from '../model/format';
import { initials, pad, parseDate, safeUrl } from '../lib/util';
import { isHex } from '../lib/color';
import { hasPhoto } from '../lib/photos';
import { Icon } from '../components/Icon';
import { MAPS_EMBED_KEY } from '../config';
import { mapArtURI, placeholderURI } from './art';
import { themeFor, type SiteMode } from './theme';
import { EditChip, Paras, Photo, type EditTarget } from './parts';

interface BlockProps { block: Block; site: Site; mode: SiteMode; onEdit?: (t: EditTarget) => void; onRsvp?: (blockId: string) => void }

export function BlockView({ block: b, site: s, mode, onEdit, onRsvp }: BlockProps) {
  const T = BLOCK_TYPES[b.type];
  let cls = '';
  let inner: React.ReactNode = null;
  switch (b.type) {
    case 'text': inner = <div className="s-prose"><Paras text={b.data.body} /></div>; break;
    case 'gallery': cls = 'wide'; inner = <Gallery b={b} s={s} />; break;
    case 'countdown': inner = <Countdown s={s} message={b.data.message} />; break;
    case 'story': {
      const img = hasPhoto(b.data.image) ? b.data.image : null;
      if (img) cls = 's-story has-img';
      inner = <>{img && <figure className="s-story-img"><Photo p={img} width={1000} /></figure>}<div className="s-prose"><Paras text={b.data.body} /></div></>;
      break;
    }
    case 'schedule':
      inner = (
        <ol className="s-sched">
          {(b.data.events || []).map((ev, i) => (
            <li key={i}><div className="t">{fmtTime(ev.time, s.design.timeFormat)}</div>
              <div><h3>{ev.name}</h3>{ev.place && <p className="pl">{ev.place}</p>}{ev.notes && <p className="nt">{ev.notes}</p>}</div></li>
          ))}
        </ol>
      );
      break;
    case 'venue': cls = 'mid'; inner = <Venue b={b} s={s} mode={mode} />; break;
    case 'dresscode': inner = <DressCode b={b} />; break;
    case 'accommodation':
      inner = (
        <>
          {b.data.intro && <p className="s-lede">{b.data.intro}</p>}
          <div className="s-places">{(b.data.places || []).map((p, i) => {
            const u = safeUrl(p.link);
            return <div className="s-place" key={i}><h3>{p.name}</h3>{p.distance && <p className="dist">{p.distance}</p>}{p.notes && <p>{p.notes}</p>}{u && <a className="s-btn" href={u} target="_blank" rel="noopener noreferrer">Book a room</a>}</div>;
          })}</div>
        </>
      );
      break;
    case 'party':
      inner = (
        <>
          {b.data.intro && <p className="s-lede">{b.data.intro}</p>}
          <div className="s-people">{(b.data.people || []).map((p, i) => (
            <div className="s-person" key={i}><div className="av" aria-hidden="true">{initials(p.name)}</div><h3>{p.name}</h3>{p.role && <p className="role">{p.role}</p>}{p.note && <p className="note">{p.note}</p>}</div>
          ))}</div>
        </>
      );
      break;
    case 'fund': {
      const u = safeUrl(b.data.link);
      inner = <><div className="s-prose"><Paras text={b.data.body} /></div>{b.data.details && <div className="s-details">{b.data.details}</div>}{u && <div className="s-actions"><a className="s-btn" href={u} target="_blank" rel="noopener noreferrer">{b.data.buttonLabel || 'Contribute'}</a></div>}</>;
      break;
    }
    case 'faq':
      inner = <div className="s-faq">{(b.data.items || []).map((it, i) => <details key={i}><summary>{it.q}</summary><div className="a"><Paras text={it.a} /></div></details>)}</div>;
      break;
    case 'photos': {
      const u = safeUrl(b.data.link);
      inner = <><div className="s-prose"><Paras text={b.data.body} /></div>{b.data.hashtag && <p className="s-tag">{b.data.hashtag}</p>}{u && <div className="s-actions"><a className="s-btn" href={u} target="_blank" rel="noopener noreferrer">{b.data.buttonLabel || 'Add your photos'}</a></div>}</>;
      break;
    }
    case 'rsvp':
      inner = (
        <>
          <RsvpHead b={b} s={s} />
          <div className="s-actions s-rsvp-cta"><button type="button" className="s-btn s-btn-lg" onClick={() => onRsvp?.(b.id)}>{b.data.buttonLabel || 'RSVP now'}</button></div>
        </>
      );
      break;
  }
  return (
    <div className={`s-block s-${b.type} ${cls}`} data-block={b.id} {...(mode === 'edit' ? { 'data-edit': '' } : {})}>
      <EditChip mode={mode} label={'Edit ' + T.label.toLowerCase()} onEdit={onEdit} target={{ kind: 'block', id: b.id }} />
      {b.showTitle && b.title && <h2 className="s-h">{b.title}</h2>}
      {inner}
    </div>
  );
}

export function RsvpHead({ b, s }: { b: BlockOf<'rsvp'>; s: Site }) {
  const deadline = fmtDate(b.data.deadline, s.design.dateFormat);
  return <>{b.data.intro && <div className="s-prose"><Paras text={b.data.intro} /></div>}{deadline && <p className="s-deadline">Kindly reply by {deadline}</p>}</>;
}

function Gallery({ b, s }: { b: BlockOf<'gallery'>; s: Site }) {
  const d = b.data;
  const { colors } = themeFor(s);
  const layout = ['strip', 'grid', 'feature'].includes(d.layout) ? d.layout : 'strip';
  const imgs = (d.images || []).filter(hasPhoto);
  const count = imgs.length || (layout === 'grid' ? 6 : 3);
  const lay = layout === 'feature' && count < 3 ? 'strip' : layout;
  const n = Math.min(count, 3);
  const width = layout === 'grid' ? 800 : 1200;
  const figs = imgs.length
    ? imgs.map((p, i) => <figure key={p.id + i}><Photo p={p} width={width} lazy /></figure>)
    : Array.from({ length: layout === 'grid' ? 6 : 3 }, (_, i) => <figure key={i}><img src={placeholderURI(i + (layout === 'feature' ? 3 : 0), colors)} alt="" /></figure>);
  return (
    <>
      <div className={`s-gal ${lay}`} style={{ '--n': n } as React.CSSProperties} data-n={n} {...(count > 1 && count % 2 ? { 'data-odd': '' } : {})}>{figs}</div>
      {d.caption && <p className="s-cap">{d.caption}</p>}
    </>
  );
}

function countdownTarget(s: Site) {
  const h = s.header;
  const p = parseDate(h.date);
  if (!p) return 0;
  const m = /^(\d{1,2}):(\d{2})/.exec(h.time || '');
  return new Date(p.y, p.m, p.d, m ? +m[1] : 12, m ? +m[2] : 0).getTime();
}

function Countdown({ s, message }: { s: Site; message: string }) {
  const target = countdownTarget(s);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = target ? Math.max(0, Math.floor((target - now) / 1000)) : -1;
  return (
    <>
      <div className="s-count" aria-live="off">
        {diff === 0 ? <p className="s-count-done">Today’s the day — or we’re already married!</p> : (
          <>
            <div><b>{diff < 0 ? '–' : Math.floor(diff / 86400)}</b><small>days</small></div>
            <div><b>{diff < 0 ? '–' : pad(Math.floor((diff % 86400) / 3600))}</b><small>hours</small></div>
            <div><b>{diff < 0 ? '–' : pad(Math.floor((diff % 3600) / 60))}</b><small>minutes</small></div>
            <div><b>{diff < 0 ? '–' : pad(diff % 60)}</b><small>seconds</small></div>
          </>
        )}
      </div>
      {message && <p className="s-count-msg">{message}</p>}
    </>
  );
}

export function venueInfo(b: BlockOf<'venue'>, s: Site) {
  const d = b.data, h = s.header;
  const name = String(d.name || '').trim() || String(h.venue || '').trim();
  const addr = String(d.address || '').trim() || String(h.city || '').trim();
  const q = [name, addr.replace(/\n+/g, ', ')].filter(Boolean).join(', ');
  const link = safeUrl(d.mapLink);
  const enc = encodeURIComponent(q);
  return {
    name, addr, q,
    mapUrl: link || `https://www.google.com/maps/search/?api=1&query=${enc}`,
    dirUrl: link || `https://www.google.com/maps/dir/?api=1&destination=${enc}`,
    embedUrl: MAPS_EMBED_KEY ? `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(MAPS_EMBED_KEY)}&q=${enc}` : '',
  };
}

function Venue({ b, s, mode }: { b: BlockOf<'venue'>; s: Site; mode: SiteMode }) {
  const v = venueInfo(b, s);
  return (
    <div className="s-venue">
      {v.name && <h3 className="s-venue-name">{v.name}</h3>}
      {v.addr && <p className="s-venue-addr">{v.addr}</p>}
      {b.data.showMap && v.q && <VenueMap v={v} s={s} mode={mode} />}
      {v.q && <div className="s-actions"><a className="s-btn" href={v.dirUrl} target="_blank" rel="noopener noreferrer"><Icon name="pin" size={18} />Get directions</a></div>}
      {b.data.notes && <div className="s-prose s-venue-notes"><Paras text={b.data.notes} /></div>}
    </div>
  );
}

function VenueMap({ v, s, mode }: { v: ReturnType<typeof venueInfo>; s: Site; mode: SiteMode }) {
  // While editing, wait for typing to settle before loading Google's map
  const [src, setSrc] = useState(mode === 'edit' ? '' : v.embedUrl);
  useEffect(() => {
    if (!v.embedUrl) return;
    if (mode !== 'edit') { setSrc(v.embedUrl); return; }
    const t = setTimeout(() => setSrc(v.embedUrl), 700);
    return () => clearTimeout(t);
  }, [v.embedUrl, mode]);
  if (v.embedUrl) {
    return <div className="s-map is-live">{src && <iframe src={src} title={`Map showing ${v.name || v.q}`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />}</div>;
  }
  const { colors } = themeFor(s);
  // The backdrop is decorative only; the card makes clear the real map opens in Google Maps
  return (
    <a className="s-map" href={v.mapUrl} target="_blank" rel="noopener noreferrer" aria-label={`View ${v.name || 'the venue'} on Google Maps (opens in a new tab)`}>
      <img src={mapArtURI(v.q, colors)} alt="" />
      <span className="s-map-card">
        <svg viewBox="0 0 40 52" width="30" height="39" aria-hidden="true"><path d="M20 51C20 51 38 31 38 19A18 18 0 0 0 2 19C2 31 20 51 20 51Z" style={{ fill: 'var(--accent)' }} /><circle cx="20" cy="19" r="7" style={{ fill: 'var(--on-accent)' }} /></svg>
        <b>View on Google Maps</b><span>Live map and directions</span><span className="s-map-go"><Icon name="external" size={14} /></span>
      </span>
    </a>
  );
}

function DressCode({ b }: { b: BlockOf<'dresscode'> }) {
  const d = b.data;
  const cols = (d.colors || []).filter((c) => c && isHex(c.hex));
  const imgs = (d.images || []).filter(hasPhoto);
  const n = imgs.length <= 3 ? imgs.length : imgs.length === 4 ? 2 : 3;
  return (
    <>
      {d.style && <p className="s-dc-style">{d.style}</p>}
      {cols.length > 0 && (
        <ul className="s-swatches" aria-label="Colours of the day">
          {cols.map((c, i) => <li key={i}><span className="sw" style={{ background: c.hex }} />{c.name ? <span className="sw-name">{c.name}</span> : <span className="sr-only">{c.hex}</span>}</li>)}
        </ul>
      )}
      {d.body && <div className="s-prose"><Paras text={d.body} /></div>}
      {n > 0 && <div className="s-dc-photos" style={{ '--n': n } as React.CSSProperties}>{imgs.map((p, k) => <figure key={p.id + k}><Photo p={p} alt={`Dress code idea ${k + 1}`} width={800} lazy /></figure>)}</div>}
    </>
  );
}
